import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, getCurrentLanguageDirect, setLanguage, SUPPORTED_LANGUAGES } from './index';

interface LanguageContextType {
  currentLanguage: Language;
  setCurrentLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  tWithFallback: (key: string, fallback?: string) => string;
  getLanguageInfo: () => {
    current: Language;
    isFrench: boolean;
    isEnglish: boolean;
  };
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguageState] = useState<Language>(getCurrentLanguageDirect());

  // Listen for language changes from other components
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: Language }>) => {
      setCurrentLanguageState(event.detail.language);
    };

    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
    };
  }, []);

  const handleLanguageChange = (language: Language) => {
    setLanguage(language);
    setCurrentLanguageState(language);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    // Import translations dynamically to avoid circular dependencies
    const { translations } = require('./translations');
    const translation = translations[key];
    
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }

    let text = translation[currentLanguage] || translation.fr || key;

    // Replace parameters if provided
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{${param}}`, 'g'), String(value));
      });
    }

    return text;
  };

  const tWithFallback = (key: string, fallback?: string): string => {
    const { translations } = require('./translations');
    const translation = translations[key];
    
    if (!translation) {
      return fallback || key;
    }

    return translation[currentLanguage] || translation.fr || fallback || key;
  };

  const getLanguageInfo = () => {
    return {
      current: currentLanguage,
      isFrench: currentLanguage === 'fr',
      isEnglish: currentLanguage === 'en'
    };
  };

  const value: LanguageContextType = {
    currentLanguage,
    setCurrentLanguage: handleLanguageChange,
    t,
    tWithFallback,
    getLanguageInfo
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
